import * as React from "react";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { searchCenterPeople } from "../../../../services/centerPeopleService";

const MIN_SEARCH_LENGTH = 2;

export const AddCenterPersonDialog = ({
  open,
  centerId,
  personType,
  saving,
  onClose,
  onAdd,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = React.useState("");
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [searchFailed, setSearchFailed] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setInputValue("");
      setSelectedUser(null);
      setOptions([]);
      setSearchFailed(false);
      return undefined;
    }

    const search = inputValue.trim();
    if (!centerId || selectedUser || search.length < MIN_SEARCH_LENGTH) {
      setOptions([]);
      setLoading(false);
      setSearchFailed(false);
      return undefined;
    }

    let active = true;
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      setSearchFailed(false);

      try {
        const users = await searchCenterPeople(centerId, personType, search);
        if (active) {
          setOptions(Array.isArray(users) ? users : []);
        }
      } catch (error) {
        if (active) {
          setOptions([]);
          setSearchFailed(true);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [centerId, inputValue, open, personType, selectedUser]);

  const title =
    personType === "monitor" ? t("addMonitor") : t("addManager");

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        {searchFailed && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("userSearchFailed")}
          </Alert>
        )}
        <Autocomplete
          options={options}
          value={selectedUser}
          inputValue={inputValue}
          loading={loading}
          disabled={saving}
          filterOptions={(availableOptions) => availableOptions}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.email || ""}
          noOptionsText={
            inputValue.trim().length < MIN_SEARCH_LENGTH
              ? t("enterEmailToSearch")
              : t("noUsersFound")
          }
          onChange={(_event, value) => setSelectedUser(value)}
          onInputChange={(_event, value, reason) => {
            setInputValue(value);
            if (reason === "input") {
              setSelectedUser(null);
            }
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.email} — {option.firstname} {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              label={t("searchUserByEmail")}
              placeholder={t("email")}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" disabled={saving} onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={!selectedUser || saving}
          onClick={() => onAdd(selectedUser)}>
          {saving ? t("loading") : t("add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCenterPersonDialog;
